<template>
    <div class="tw-w-full tw-h-full">
        <!-- Filters and Search -->
        <div class="tw-bg-white tw-rounded-lg  tw-border-gray-200 tw-mb-6">
            <div class="tw-flex tw-flex-col md:tw-flex-row tw-gap-4 tw-items-start md:tw-items-center tw-justify-between">
                <!-- Search -->
                <div class="tw-flex-1 tw-max-w-md">
                    <FormField
                        v-model="searchQuery"
                        type="text"
                        :placeholder="$t('common.search-connections', 'Search connections...')"
                        left-icon="solar:magnifer-bold"
                        :hide-errors="true"
                        :hide-label="true"
                        @input="handleSearch"
                    />
                </div>
                
                <!-- Status Filter -->
                <div class="tw-flex tw-gap-2">
                    <a-button
                        v-for="status in statusFilters"
                        :key="status.value"
                        :intent="selectedStatus === status.value ? 'primary' : 'light'"
                        size="sm"
                        @click="handleStatusFilter(status.value)"
                    >
                        {{ status.label }}
                    </a-button>
                </div>
            </div>
        </div>
        
        <!-- Stats Section -->
        <div class="tw-mb-4">
            <div class="tw-flex tw-gap-3 tw-text-sm">
                <span class="tw-text-gray-600">Connections: <span class="tw-font-medium tw-text-gray-900">{{ stats.total_connections || 0 }}</span></span>
                <span class="tw-text-gray-600">Pending: <span class="tw-font-medium tw-text-gray-900">{{ (stats.pending_sent || 0) + (stats.pending_received || 0) }}</span></span>
                <span class="tw-text-gray-600">Declined: <span class="tw-font-medium tw-text-gray-900">{{ stats.declined_requests || 0 }}</span></span>
            </div>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="tw-space-y-4">
            <div v-for="i in 6" :key="i" class="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-p-4">
                <div class="tw-flex tw-items-start tw-justify-between tw-mb-4">
                    <div class="tw-flex tw-items-center tw-gap-3">
                        <div class="tw-w-12 tw-h-12 tw-bg-gray-200 tw-rounded-full tw-animate-pulse"></div>
                        <div class="tw-flex-1">
                            <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-mb-2 tw-animate-pulse"></div>
                            <div class="tw-h-3 tw-bg-gray-200 tw-rounded tw-w-24 tw-animate-pulse"></div>
                        </div>
                    </div>
                    <div class="tw-w-16 tw-h-6 tw-bg-gray-200 tw-rounded tw-animate-pulse"></div>
                </div>
                <div class="tw-space-y-2">
                    <div class="tw-h-3 tw-bg-gray-200 tw-rounded tw-animate-pulse"></div>
                    <div class="tw-h-3 tw-bg-gray-200 tw-rounded tw-w-32 tw-animate-pulse"></div>
                </div>
            </div>
        </div>

        <!-- Connections List -->
        <div v-else-if="connections.length > 0" class="tw-space-y-4">
            <ConnectionCard
                v-for="connection in connections"
                :key="connection.id"
                :connection="connection"
                @removed="handleConnectionRemoved"
                @cancelled="handleConnectionCancelled"
                @accepted="handleConnectionAccepted"
                @declined="handleConnectionDeclined"
            />
        </div>

        <!-- Empty State -->
        <div v-else class="tw-text-center tw-py-12">
            <div class="tw-max-w-md tw-mx-auto">
                <div class="tw-w-24 tw-h-24 tw-bg-gray-100 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mx-auto tw-mb-4">
                    <icon icon="solar:users-group-rounded-bold" class="tw-text-4xl tw-text-gray-400" />
                </div>
                <h3 class="tw-text-lg tw-font-medium tw-text-gray-900 tw-mb-2">
                    {{ $t('messages.no-connections-found', 'No connections found') }}
                </h3>
                <p class="tw-text-gray-500 tw-mb-6">
                    {{ getEmptyStateMessage() }}
                </p>
                <a-button
                    v-if="selectedStatus === 'all'"
                    as="router-link"
                    :to="{ name: 'shared.community' }"
                    intent="primary"
                    left-icon="solar:users-group-rounded-bold"
                >
                    {{ $t('buttons.find-people', 'Find People') }}
                </a-button>
                <a-button
                    v-else
                    intent="primary"
                    left-icon="solar:refresh-bold"
                    @click="resetFilters"
                >
                    {{ $t('buttons.reset-filters', 'Reset Filters') }}
                </a-button>
            </div>
        </div>

        <!-- Pagination -->
        <div v-if="connections.length > 0" class="tw-mt-6">
            <TablePagination
                :has-next="!!pagination.next_page_url"
                :has-previous="!!pagination.prev_page_url"
                @next="loadNextPage"
                @previous="loadPreviousPage"
            />
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import useAlert from '@/composables/useAlert';
import connectionsApi from '@/api/connections';
import ConnectionCard from '@/components/my-eshow/ConnectionCard.vue';
import TablePagination from '@/components/table/TablePagination.vue';
import FormField from '@/components/form/FormField.vue';

const { t } = useI18n();

// Reactive data
const loading = ref(false);
const connections = ref([]);
const pagination = ref({});
const stats = ref({});
const searchQuery = ref('');
const selectedStatus = ref('all');
const currentPage = ref(1);

// Status filters
const statusFilters = [
    { value: 'all', label: 'All' },
    { value: 'established', label: 'Connected' },
    { value: 'pending', label: 'Pending' },
    { value: 'sent', label: 'Sent' },
    { value: 'received', label: 'Received' }
];


// Methods
const getMyConnections = async (page = 1) => {
    loading.value = true;
    try {
        const params = {
            page,
            per_page: 10
        };

        // Add status filter
        if (selectedStatus.value !== 'all') {
            params.type = selectedStatus.value;
        }

        // Add search query
        if (searchQuery.value.trim()) {
            params.search = searchQuery.value.trim();
        }

        let response = await connectionsApi.paginate(params);
        response = response.data;
        
        // Debug logging
        console.log('API Response:', response);
        console.log('Response data:', response.data);
        console.log('Response meta:', response.meta);
        
        // Check the actual structure
        if (response.data && response.data.data) {
            console.log('Connections found in response.data.data');
            connections.value = response.data.data;
        } else if (response.data && Array.isArray(response.data)) {
            console.log('Connections found directly in response.data');
            connections.value = response.data;
        } else {
            console.log('No connections found, response.data:', response.data);
            connections.value = [];
        }
        
        // Handle pagination
        if (response.data && response.data.current_page) {
            console.log('Pagination found in response.data');
            pagination.value = {
                current_page: response.data.current_page,
                last_page: response.data.last_page,
                next_page_url: response.data.next_page_url,
                prev_page_url: response.data.prev_page_url,
                total: response.data.total
            };
        } else {
            console.log('No pagination found in response.data');
            pagination.value = {};
        }
        
        // Update stats if available
        if (response.meta && response.meta.stats) {
            console.log('Stats found in response.meta.stats');
            stats.value = response.meta.stats;
        } else {
            console.log('No stats found in response.meta');
            stats.value = {};
        }
        
        currentPage.value = page;
    } catch (error) {
        useAlert(t('errors.something-went-wrong'), 'error');
    } finally {
        loading.value = false;
    }
};

const handleSearch = () => {
    currentPage.value = 1;
    getMyConnections(1);
};

const handleStatusFilter = (status) => {
    selectedStatus.value = status;
    currentPage.value = 1;
    getMyConnections(1);
};

const resetFilters = () => {
    searchQuery.value = '';
    selectedStatus.value = 'all';
    currentPage.value = 1;
    getMyConnections(1);
};

const loadNextPage = () => {
    if (pagination.value.next_page_url) {
        getMyConnections(currentPage.value + 1);
    }
};

const loadPreviousPage = () => {
    if (pagination.value.prev_page_url) {
        getMyConnections(currentPage.value - 1);
    }
};

const handleConnectionRemoved = (connectionId) => {
    connections.value = connections.value.filter(c => c.id !== connectionId);
    // Refresh stats
    getMyConnections(currentPage.value);
};

const handleConnectionCancelled = (connectionId) => {
    connections.value = connections.value.filter(c => c.id !== connectionId);
    // Refresh stats
    getMyConnections(currentPage.value);
};

const handleConnectionAccepted = (connectionId) => {
    // Update the connection status in the list
    const connection = connections.value.find(c => c.id === connectionId);
    if (connection) {
        connection.status = 'accepted';
        connection.display_status = {
            label: 'Connected',
            description: 'Connection request has been accepted',
            color: 'success'
        };
        connection.actions = ['remove'];
    }
    // Refresh stats
    getMyConnections(currentPage.value);
};

const handleConnectionDeclined = (connectionId) => {
    connections.value = connections.value.filter(c => c.id !== connectionId);
    // Refresh stats
    getMyConnections(currentPage.value);
};

const getEmptyStateMessage = () => {
    if (selectedStatus.value !== 'all') {
        return t('messages.no-connections-for-filter', 'No connections found for the selected filter.');
    }
    if (searchQuery.value.trim()) {
        return t('messages.no-connections-for-search', 'No connections found matching your search.');
    }
    return t('messages.no-connections-description', 'Start connecting with other users and they\'ll appear here.');
};

// Lifecycle
onMounted(() => {
    getMyConnections();
});
</script>

<style scoped>
</style>